import { useMemo } from 'react';
import {
  asBool,
  asString,
  cloneDoc,
  ensureObject,
  objectMap,
  stringList,
  type DefaultParamsDoc,
  type MergeRule,
  type ParamDefinition,
  type PathEntry,
} from '../../../lib/default-params-form-utils';
import {
  CheckboxField,
  FieldLabel,
  ItemCard,
  RemoveButton,
  SectionBox,
  StringListEditor,
  SubsectionTitle,
  TextInput,
} from '../tenant-config-form/FormPrimitives';

interface DefaultParamsFormProps {
  doc: DefaultParamsDoc;
  onChange: (doc: DefaultParamsDoc) => void;
}

const PARAM_TYPES = [
  'tenant-select',
  'dynamic-select',
  'boolean',
  'string',
  'number',
  'choice',
] as const;

function updateDoc(onChange: (doc: DefaultParamsDoc) => void, doc: DefaultParamsDoc, mutator: (next: DefaultParamsDoc) => void) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

function MergeRulesEditor({
  rules,
  onChange,
}: {
  rules: MergeRule[];
  onChange: (rules: MergeRule[]) => void;
}) {
  const add = () => onChange([...rules, { at: '' }]);
  const update = (idx: number, patch: Partial<MergeRule>) => {
    const next = [...rules];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => {
    const next = [...rules];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {rules.length === 0 && <p className="text-xs text-gray-500">No merge rules.</p>}
      {rules.map((rule, idx) => (
        <ItemCard key={`merge-${idx}`}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-[11px] font-medium text-gray-500">Rule {idx + 1}</span>
            <RemoveButton onClick={() => remove(idx)} label="Remove merge rule" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FieldLabel label="at">
              <TextInput value={asString(rule.at)} onChange={(v) => update(idx, { at: v })} mono />
            </FieldLabel>
            <FieldLabel label="as">
              <TextInput value={asString(rule.as)} onChange={(v) => update(idx, { as: v || undefined })} mono />
            </FieldLabel>
            <FieldLabel label="from_master">
              <TextInput
                value={asString(rule.from_master)}
                onChange={(v) => update(idx, { from_master: v || undefined })}
                mono
              />
            </FieldLabel>
            <FieldLabel label="from_tenant">
              <TextInput
                value={asString(rule.from_tenant)}
                onChange={(v) => update(idx, { from_tenant: v || undefined })}
                mono
              />
            </FieldLabel>
          </div>
        </ItemCard>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
      >
        + Add merge rule
      </button>
    </div>
  );
}

function ParamDefinitionEditor({
  name,
  def,
  onChange,
  onRemove,
}: {
  name: string;
  def: ParamDefinition;
  onChange: (def: ParamDefinition) => void;
  onRemove?: () => void;
}) {
  const visibleWhen = def.visible_when;
  const equalsValue = Array.isArray(visibleWhen?.equals)
    ? visibleWhen.equals.join(', ')
    : asString(visibleWhen?.equals);

  return (
    <ItemCard>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold font-mono text-gray-800 dark:text-gray-200">{name}</span>
        {onRemove ? <RemoveButton onClick={onRemove} label={`Remove param ${name}`} /> : null}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <FieldLabel label="Type">
          <select
            value={asString(def.type)}
            onChange={(e) => onChange({ ...def, type: e.target.value || undefined })}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-xs"
          >
            <option value="">—</option>
            {PARAM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FieldLabel>
        <FieldLabel label="Label">
          <TextInput value={asString(def.label)} onChange={(v) => onChange({ ...def, label: v || undefined })} />
        </FieldLabel>
        <FieldLabel label="Default">
          <TextInput value={asString(def.default)} onChange={(v) => onChange({ ...def, default: v || undefined })} />
        </FieldLabel>
        <FieldLabel label="Source">
          <TextInput value={asString(def.source)} onChange={(v) => onChange({ ...def, source: v || undefined })} mono />
        </FieldLabel>
        <FieldLabel label="Path">
          <TextInput value={asString(def.path)} onChange={(v) => onChange({ ...def, path: v || undefined })} mono />
        </FieldLabel>
        <div className="flex flex-col gap-2 justify-end">
          <CheckboxField
            label="Required"
            checked={asBool(def.required)}
            onChange={(v) => onChange({ ...def, required: v })}
          />
          <CheckboxField
            label="Hidden"
            checked={asBool(def.hidden)}
            onChange={(v) => onChange({ ...def, hidden: v })}
          />
        </div>
        <FieldLabel label="Depends on" className="md:col-span-2">
          <StringListEditor
            items={stringList(def.depends_on)}
            onChange={(items) =>
              onChange({
                ...def,
                depends_on: items.filter((s) => s.trim()).length ? items.filter((s) => s.trim()) : undefined,
              })
            }
            placeholder="param name"
          />
        </FieldLabel>
        <FieldLabel label="Choices" className="md:col-span-2">
          <StringListEditor
            items={stringList(def.choices)}
            onChange={(items) =>
              onChange({
                ...def,
                choices: items.filter((s) => s.trim()).length ? items.filter((s) => s.trim()) : undefined,
              })
            }
            placeholder="choice value"
          />
        </FieldLabel>
        <FieldLabel label="Visible when param">
          <TextInput
            value={asString(visibleWhen?.param)}
            onChange={(v) =>
              onChange({
                ...def,
                visible_when: v.trim()
                  ? { param: v.trim(), equals: visibleWhen?.equals ?? '' }
                  : undefined,
              })
            }
          />
        </FieldLabel>
        <FieldLabel label="Visible when equals">
          <TextInput
            value={equalsValue}
            onChange={(v) => {
              if (!visibleWhen?.param) return;
              const parts = v.split(',').map((s) => s.trim()).filter(Boolean);
              onChange({
                ...def,
                visible_when: {
                  param: visibleWhen.param,
                  equals: parts.length > 1 ? parts : parts[0] ?? '',
                },
              });
            }}
            placeholder="value or comma-separated"
          />
        </FieldLabel>
      </div>
    </ItemCard>
  );
}

function ParamsMapEditor({
  params,
  onChange,
  allowAdd,
}: {
  params: Record<string, ParamDefinition>;
  onChange: (params: Record<string, ParamDefinition>) => void;
  allowAdd?: boolean;
}) {
  const names = Object.keys(params).sort();

  const addParam = () => {
    const name = window.prompt('Parameter name');
    if (!name?.trim() || params[name.trim()]) return;
    onChange({ ...params, [name.trim()]: {} });
  };

  return (
    <div className="space-y-2">
      {names.length === 0 && <p className="text-xs text-gray-500">No parameter overrides.</p>}
      {names.map((name) => (
        <ParamDefinitionEditor
          key={name}
          name={name}
          def={params[name] || {}}
          onChange={(def) => onChange({ ...params, [name]: def })}
          onRemove={allowAdd ? () => {
            const next = { ...params };
            delete next[name];
            onChange(next);
          } : undefined}
        />
      ))}
      {allowAdd ? (
        <button type="button" onClick={addParam} className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline">
          + Add parameter
        </button>
      ) : null}
    </div>
  );
}

function PathEntryEditor({
  name,
  entry,
  onChange,
  onRemove,
  showCategory,
  showParams,
}: {
  name: string;
  entry: PathEntry;
  onChange: (entry: PathEntry) => void;
  onRemove?: () => void;
  showCategory?: boolean;
  showParams?: boolean;
}) {
  const mergeRules = Array.isArray(entry.merge) ? (entry.merge as MergeRule[]) : [];

  return (
    <ItemCard>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold font-mono text-gray-900 dark:text-gray-100">{name}</span>
        {onRemove ? <RemoveButton onClick={onRemove} label={`Remove ${name}`} /> : null}
      </div>
      <div className="space-y-3 pt-2">
        {showCategory ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FieldLabel label="Category">
              <TextInput
                value={asString(entry.category)}
                onChange={(v) => onChange({ ...entry, category: v || undefined })}
              />
            </FieldLabel>
            <FieldLabel label="Config type">
              <select
                value={asString(entry.config_type) || 'tenant-aware'}
                onChange={(e) => onChange({ ...entry, config_type: e.target.value })}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-xs"
              >
                <option value="tenant-aware">tenant-aware</option>
                <option value="manual">manual</option>
              </select>
            </FieldLabel>
          </div>
        ) : (
          <CheckboxField
            label="Tenant required"
            checked={asBool(entry.tenant_required)}
            onChange={(v) => onChange({ ...entry, tenant_required: v })}
          />
        )}

        <SubsectionTitle>Merge rules</SubsectionTitle>
        <MergeRulesEditor
          rules={mergeRules}
          onChange={(rules) => onChange({ ...entry, merge: rules })}
        />

        {showParams ? (
          <>
            <SubsectionTitle>Parameter overrides</SubsectionTitle>
            <ParamsMapEditor
              params={entry.params || {}}
              onChange={(params) => onChange({ ...entry, params: Object.keys(params).length ? params : undefined })}
              allowAdd
            />
          </>
        ) : null}
      </div>
    </ItemCard>
  );
}

export function DefaultParamsForm({ doc, onChange }: DefaultParamsFormProps) {
  const globalParams = useMemo(() => objectMap(doc.global_params as Record<string, unknown>), [doc.global_params]);
  const workflows = useMemo(() => objectMap(doc.workflows as Record<string, unknown>), [doc.workflows]);
  const cliCommands = useMemo(() => objectMap(doc.cli_commands as Record<string, unknown>), [doc.cli_commands]);

  const globalParamNames = Object.keys(globalParams).sort();
  const workflowNames = Object.keys(workflows).sort();
  const cliNames = Object.keys(cliCommands).sort();

  const addWorkflow = () => {
    const name = window.prompt('Workflow template name');
    if (!name?.trim() || workflows[name.trim()]) return;
    updateDoc(onChange, doc, (next) => {
      ensureObject(next, 'workflows')[name.trim()] = { category: '', config_type: 'tenant-aware', merge: [] };
    });
  };

  const addCliCommand = () => {
    const name = window.prompt('CLI command name');
    if (!name?.trim() || cliCommands[name.trim()]) return;
    updateDoc(onChange, doc, (next) => {
      ensureObject(next, 'cli_commands')[name.trim()] = { merge: [] };
    });
  };

  const addGlobalParam = () => {
    const name = window.prompt('Global parameter name');
    if (!name?.trim() || globalParams[name.trim()]) return;
    updateDoc(onChange, doc, (next) => {
      ensureObject(next, 'global_params')[name.trim()] = {};
    });
  };

  return (
    <div className="space-y-4 text-sm">
      <SectionBox title="Descriptor">
        <FieldLabel label="Version">
          <TextInput
            value={asString(doc.version)}
            onChange={(v) => updateDoc(onChange, doc, (n) => { n.version = v; })}
          />
        </FieldLabel>
      </SectionBox>

      <SectionBox title="Global parameters" onAdd={addGlobalParam} addLabel="Add param">
        {globalParamNames.length === 0 ? (
          <p className="text-xs text-gray-500">No global parameters defined.</p>
        ) : (
          globalParamNames.map((name) => (
            <ParamDefinitionEditor
              key={name}
              name={name}
              def={(globalParams[name] as ParamDefinition) || {}}
              onChange={(def) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(n, 'global_params')[name] = def;
                })
              }
              onRemove={() =>
                updateDoc(onChange, doc, (n) => {
                  const map = ensureObject(n, 'global_params');
                  delete map[name];
                })
              }
            />
          ))
        )}
      </SectionBox>

      <SectionBox title="Workflows" onAdd={addWorkflow} addLabel="Add workflow">
        {workflowNames.length === 0 ? (
          <p className="text-xs text-gray-500">No workflows defined.</p>
        ) : (
          workflowNames.map((name) => (
            <PathEntryEditor
              key={name}
              name={name}
              entry={(workflows[name] as PathEntry) || {}}
              onChange={(entry) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(n, 'workflows')[name] = entry;
                })
              }
              onRemove={() =>
                updateDoc(onChange, doc, (n) => {
                  const map = ensureObject(n, 'workflows');
                  delete map[name];
                })
              }
              showCategory
              showParams
            />
          ))
        )}
      </SectionBox>

      <SectionBox title="CLI commands" onAdd={addCliCommand} addLabel="Add command">
        {cliNames.length === 0 ? (
          <p className="text-xs text-gray-500">No CLI commands defined.</p>
        ) : (
          cliNames.map((name) => (
            <PathEntryEditor
              key={name}
              name={name}
              entry={(cliCommands[name] as PathEntry) || {}}
              onChange={(entry) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(n, 'cli_commands')[name] = entry;
                })
              }
              onRemove={() =>
                updateDoc(onChange, doc, (n) => {
                  const map = ensureObject(n, 'cli_commands');
                  delete map[name];
                })
              }
            />
          ))
        )}
      </SectionBox>

      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        Legacy master_paths / tenant_paths fields are preserved when round-tripping via YAML. Use the YAML tab for bulk edits or deprecated fields.
      </p>
    </div>
  );
}
